import { useState, type ReactNode } from 'react';
import { Copy, Check } from 'lucide-react';

// Renderer Markdown minimo per le guide in-app (frontend/src/guides). Volutamente senza
// dipendenze: il sottoinsieme che serve a una procedura operativa è piccolo e noto —
// titoli (##/###), paragrafi, elenchi puntati e numerati, blocchi di codice con pulsante
// "Copy", tabelle, citazioni (usate come riquadri "Dove" / "Attenzione"), righe orizzontali;
// inline: **grassetto**, *corsivo*, `codice`, [link](url). Niente HTML grezzo: il contenuto
// viene dal repo, non dagli utenti, ma non c'è motivo di aprire quella porta.

export interface Heading { id: string; text: string; level: number }

export function slugify(text: string): string {
  return text.toLowerCase().replace(/[`*_]/g, '').replace(/[^a-z0-9àèéìòù]+/g, '-').replace(/^-+|-+$/g, '');
}

// Indice dei titoli di secondo livello, per la barra laterale della pagina.
export function extractHeadings(markdown: string): Heading[] {
  const out: Heading[] = [];
  let inFence = false;
  for (const line of markdown.split('\n')) {
    if (line.startsWith('```')) { inFence = !inFence; continue; }
    if (inFence) continue;
    const m = line.match(/^(#{2,3})\s+(.+)$/);
    if (m) out.push({ id: slugify(m[2]), text: m[2].replace(/[`*]/g, ''), level: m[1].length });
  }
  return out;
}

function renderInline(text: string): ReactNode[] {
  // Prima i code span (dentro non si formatta nulla), poi link, grassetto, corsivo.
  const parts: ReactNode[] = [];
  const re = /(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('`')) {
      parts.push(<code key={key++} className="px-1 py-0.5 rounded bg-primary-50 text-primary-900 text-[0.9em] font-mono">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const lm = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/)!;
      parts.push(<a key={key++} href={lm[2]} target="_blank" rel="noreferrer" className="text-primary-700 underline">{renderInline(lm[1])}</a>);
    } else if (token.startsWith('**')) {
      parts.push(<strong key={key++} className="font-semibold text-gray-900">{renderInline(token.slice(2, -2))}</strong>);
    } else {
      parts.push(<em key={key++}>{renderInline(token.slice(1, -1))}</em>);
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard non disponibile (http senza TLS): l'utente seleziona a mano */ }
  }
  const lines = code.split('\n');
  // "Copy" solo sui blocchi di comandi: un diagramma o l'output atteso di un log non si incolla
  // da nessuna parte, e il pulsante coprirebbe la fine delle righe lunghe.
  const copyable = lang === 'bash' || lang === 'sh';
  return (
    <div className="relative group my-3">
      <pre className={`bg-gray-900 text-gray-100 rounded-lg px-4 py-3 text-[13px] leading-relaxed overflow-x-auto font-mono ${copyable ? 'pr-20' : ''}`}>
        {lines.map((line, i) => {
          // Nei blocchi bash i commenti (# ...) vanno in un grigio più tenue: aiutano a leggere
          // cosa fa ogni comando senza confondersi con i comandi stessi.
          const hash = lang === 'bash' ? line.indexOf('#') : -1;
          const isComment = hash >= 0 && line.slice(0, hash).trim() === '';
          const inlineComment = hash > 0 && !isComment && /\s#\s/.test(line);
          if (isComment) return <div key={i} className="text-gray-500">{line}</div>;
          if (inlineComment) {
            const at = line.search(/\s#\s/);
            return <div key={i}>{line.slice(0, at)}<span className="text-gray-500">{line.slice(at)}</span></div>;
          }
          return <div key={i}>{line || ' '}</div>;
        })}
      </pre>
      {copyable && (
        <button onClick={copy} type="button" title="Copy to clipboard"
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-gray-800 text-gray-300 border border-gray-700 hover:text-white hover:border-gray-500 print:hidden">
          {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      )}
    </div>
  );
}

function Table({ rows }: { rows: string[][] }) {
  const [head, ...body] = rows;
  return (
    <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr>{head.map((c, i) => <th key={i} className="table-header">{renderInline(c)}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className="border-t border-gray-100 align-top">
              {r.map((c, j) => <td key={j} className={`table-cell ${j === 0 ? 'font-medium' : 'text-gray-700'}`}>{renderInline(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function splitTableRow(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
}

// Citazione: la prima riga "**Attenzione**" la rende un avviso ambra; "Dove: ..." (o qualunque
// altra cosa) un riquadro neutro di contesto. Il contenuto può includere un elenco puntato.
function Quote({ lines }: { lines: string[] }) {
  const warning = /^\*\*(attenzione|warning|nota|note)\*\*$/i.test(lines[0].trim());
  const bullets = lines.filter(l => l.startsWith('- '));
  const text = lines.filter(l => !l.startsWith('- ') && !(warning && l === lines[0]));
  return (
    <div className={`my-3 rounded-r-lg border-l-4 px-4 py-2.5 text-sm ${warning ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-primary-300 bg-primary-50/60 text-gray-700'}`}>
      {warning && <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 mb-1">{lines[0].replace(/\*/g, '')}</div>}
      {text.map((l, i) => <p key={i} className="my-0.5">{renderInline(l)}</p>)}
      {bullets.length > 0 && (
        <ul className="list-disc pl-5 mt-1 space-y-1">{bullets.map((b, i) => <li key={i}>{renderInline(b.slice(2))}</li>)}</ul>
      )}
    </div>
  );
}

export default function Markdown({ source }: { source: string }) {
  const lines = source.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('```')) {
      const lang = line.slice(3).trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) buf.push(lines[i++]);
      i++;
      blocks.push(<CodeBlock key={key++} code={buf.join('\n')} lang={lang} />);
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2];
      const id = slugify(text);
      if (level <= 2) blocks.push(<h2 key={key++} id={id} className="text-lg font-semibold text-gray-900 mt-8 mb-2 scroll-mt-20">{renderInline(text)}</h2>);
      else blocks.push(<h3 key={key++} id={id} className="text-base font-semibold text-gray-900 mt-5 mb-1.5 scroll-mt-20">{renderInline(text)}</h3>);
      i++;
      continue;
    }

    if (/^---+\s*$/.test(line)) { blocks.push(<hr key={key++} className="my-6 border-gray-200" />); i++; continue; }

    if (line.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!/^\|\s*:?-+/.test(lines[i])) rows.push(splitTableRow(lines[i]));
        i++;
      }
      blocks.push(<Table key={key++} rows={rows} />);
      continue;
    }

    if (line.startsWith('>')) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) buf.push(lines[i++].replace(/^>\s?/, ''));
      blocks.push(<Quote key={key++} lines={buf} />);
      continue;
    }

    if (/^- /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^- /.test(lines[i])) items.push(lines[i++].slice(2));
      blocks.push(<ul key={key++} className="list-disc pl-6 my-2 space-y-1 text-sm text-gray-700">{items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}</ul>);
      continue;
    }

    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) items.push(lines[i++].replace(/^\d+\. /, ''));
      blocks.push(<ol key={key++} className="list-decimal pl-6 my-2 space-y-1 text-sm text-gray-700">{items.map((it, k) => <li key={k}>{renderInline(it)}</li>)}</ol>);
      continue;
    }

    if (line.trim() === '') { i++; continue; }

    const buf: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && !/^(```|#{1,3} |\||>|- |\d+\. |---)/.test(lines[i])) buf.push(lines[i++]);
    blocks.push(<p key={key++} className="my-2 text-sm text-gray-700 leading-relaxed">{renderInline(buf.join(' '))}</p>);
  }

  return <div className="max-w-none">{blocks}</div>;
}
